import React, { useState, useCallback, ChangeEvent } from 'react';
import debounce from 'lodash/debounce';
import chunk from 'lodash/chunk';
import classnames from 'classnames';
import { CheckboxField } from '@kaizen/draft-form';
import { TextAreaField } from '@kaizen/components';
import { CommentableSelectOption, CheckedOption } from '../../types';
import { OTHER_OPTION_ID } from '../../constants';
import styles from '../CommentableSelectQuestion.scss';

type Props = {
  questionId: string;
  options: CommentableSelectOption[];
  selectedOptionIds: string[];
  selectionLimit: number;
  saveCheck: (optionList: CheckedOption[], otherComment?: string) => void;
  saveComment: (path: string, comment: string) => void;
  deleteComment: (path: string) => void;
  uiText: {
    otherLabel: string;
    regularLabel: string;
    selectionLimitError: string;
    forLabel: string;
  };
};

const CommentableSelectOptions = (props: Props) => {
  const {
    deleteComment,
    options,
    questionId,
    saveCheck,
    saveComment,
    selectionLimit,
    selectedOptionIds,
    uiText,
  } = props;
  const [checkedOptionIds, setChecked] = useState<string[]>(selectedOptionIds);
  const [comments, setComments] = useState<{ [key: string]: string }>(
    Object.assign({}, ...options.map((o) => ({ [o.id]: o.comment })))
  );
  const splitOptions = chunk(options, Math.round(options.length / 2));

  const overSelectionLimit = (): boolean => {
    if (selectionLimit === -1 || checkedOptionIds.length < selectionLimit) {
      return false;
    }
    alert(uiText.selectionLimitError);
    return true;
  };

  const onCheck = (optionId: string, check: boolean, commentPath: string) => {
    if (check && overSelectionLimit()) {
      return;
    }

    // Update state
    if (!check) {
      setChecked(checkedOptionIds.filter((oid) => oid !== optionId));
    } else {
      setChecked([...checkedOptionIds, optionId]);
    }

    // Prepare data for request
    const optionsChecked = options.map((o) => ({
      id: o.id,
      checked: o.id === optionId ? check : checkedOptionIds.indexOf(o.id) > -1,
    }));

    // A bit infuriatingly, we have to do some work here to add the 'Other' comment to the request if it exists and it's checked
    const otherOptionChecked =
      checkedOptionIds.indexOf(OTHER_OPTION_ID) > -1 ||
      (optionId === OTHER_OPTION_ID && check);

    saveCheck(
      optionsChecked,
      otherOptionChecked ? comments[OTHER_OPTION_ID] : undefined
    );

    if (optionId !== OTHER_OPTION_ID) {
      if (!check) {
        deleteComment(commentPath);
      } else if (comments[optionId]) {
        // There's a comment that's stored in React state that needs to be restored to the DB
        saveComment(commentPath, comments[optionId]);
      }
    }
  };

  const onCommentChange = (
    event: ChangeEvent<HTMLTextAreaElement>,
    optionId: string,
    path: string
  ) => {
    setComments({ ...comments, [optionId]: event.target.value });

    debouncedCommentSave(
      event.target.value,
      path,
      optionId === OTHER_OPTION_ID
    );
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedCommentSave = useCallback(
    debounce(
      (comment: string, path: string, other: boolean) => {
        if (other) {
          // 'Other' comments are saved differently from the others,
          // it's saved with the same query that saves check/unchecks
          const optionsChecked = options.map((o) => ({
            id: o.id,
            checked: checkedOptionIds.indexOf(o.id) > -1,
          }));
          saveCheck(optionsChecked, comment);
        } else {
          saveComment(path, comment);
        }
      },
      1000,
      { maxWait: 10000 }
    ),
    [options, checkedOptionIds, saveCheck, saveComment]
  );

  const onCommentBlur = () => debouncedCommentSave.flush();

  return (
    <>
      {splitOptions.map((columnOptions, colIndex) => (
        <div key={colIndex} className={styles.column}>
          {columnOptions.map((option) => {
            const checkedStatus = checkedOptionIds.indexOf(option.id) > -1;
            const textAreaLabel =
              option.id === OTHER_OPTION_ID
                ? uiText.otherLabel
                : uiText.regularLabel;
            return (
              <div
                className={classnames(styles.option, {
                  [styles.selected]: checkedStatus,
                })}
                key={option.id}
                data-automation-id={`commentable-select-option-wrapper-${option.id}`}
              >
                <CheckboxField
                  id={`${questionId}-${option.id}`}
                  name={option.title}
                  labelText={
                    <>
                      <span className={styles.optionTitle}>{option.title}</span>{' '}
                      {option.description}
                    </>
                  }
                  checkedStatus={checkedStatus ? 'on' : 'off'}
                  noBottomMargin
                  onCheck={() =>
                    onCheck(option.id, !checkedStatus, option.path)
                  }
                />
                {checkedOptionIds.includes(option.id) && (
                  <div className={styles.textAreaContainer}>
                    <TextAreaField
                      aria-label={`${textAreaLabel} ${uiText.forLabel} ${option.title}`}
                      id={`${questionId}-${option.id}`}
                      labelText={textAreaLabel}
                      rows={2}
                      value={comments[option.id]}
                      autogrow
                      inline
                      onChange={(event) =>
                        onCommentChange(event, option.id, option.path)
                      }
                      onBlur={onCommentBlur}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </>
  );
};

export default CommentableSelectOptions;
